import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API, FAILED, SUCCESS, USE_CASE } from '../../constants';
import {
  getApisTestData,
  getSelectedApis,
  getSelectedApiTests,
  getSelectedOptions,
  getSelectedUseCaseTests,
  getUseCasesResources,
  getUseCasesV2,
  getUseCaseTestData,
  postApiTestData,
  postUseCaseTestData,
  selectUseCase,
} from '../../services/useCasesV2';
import {
  IApiTestsResponse,
  TTestTypes,
  TUseCasesApiTags,
  TUseCaseTags,
} from '../../types';
import {
  BulkSelectUseCasePayload,
  GroupedUseCase,
  IPostUseCaseVariables,
  ISelectedUseCaseApi,
  IUseCaseResource,
  PLATFORMS,
  UC_STATUS_HIERARCHY,
} from '../../types/useCasesV2';

export const useGetUseCasesV2 = (envId: string | undefined) => {
  return useQuery({
    queryKey: ['get_use_cases_v2', envId],
    queryFn: () => getUseCasesV2(envId),
    enabled: Boolean(envId),
    select: rawData => {
      const uniqueTags = new Set();
      const groupedMap = rawData.reduce((acc, current) => {
        const { tag, environment_id, platform, id, status, carrier_selected } =
          current;

        if (!acc[tag]) {
          acc[tag] = {
            tag,
            environment_id,
            status,
            IOS: null,
            ANDROID: null,
          };
        } else {
          const currentHighestWeight =
            UC_STATUS_HIERARCHY[acc[tag].status] || 0;
          const incomingWeight = UC_STATUS_HIERARCHY[status] || 0;

          if (incomingWeight > currentHighestWeight) {
            acc[tag].status = status;
          }
        }

        if (platform === PLATFORMS.IOS || platform === PLATFORMS.ANDROID) {
          acc[tag][platform] = {
            id,
            status,
            carrier_selected,
          };
          if (carrier_selected) {
            uniqueTags.add(tag);
          }
        }

        return acc;
      }, {} as Record<string, GroupedUseCase>);

      return {
        selectedTags: Array.from(uniqueTags) as string[],
        groupedList: Object.values(groupedMap),
        groupedMap,
        rawData,
      };
    },
    retry: 1,
  });
};

export const useGetUseCasesInfo = (envId: string | undefined) => {
  return useQuery({
    queryKey: ['get_use_cases_info_v2', envId],
    queryFn: () => getUseCasesResources(envId),
    select: data => {
      return data.reduce((acc, currentUseCase) => {
        acc[currentUseCase.tag] = currentUseCase;
        return acc;
      }, {} as Record<string, IUseCaseResource>);
    },
    enabled: Boolean(envId),
    retry: 1,
  });
};

export const useGetSelectedApis = (envId: string | undefined) => {
  const { data } = useGetUseCasesV2(envId);

  return useQuery({
    queryKey: ['get_selected_apis_v2', envId],
    queryFn: () => getSelectedApis(envId),
    retry: 1,
    enabled: Boolean(envId),
    select: useCaseApiSelected => {
      const groupedMap = data?.groupedMap || {};
      const uniqueGroups = new Set<string>();

      const apisSelected = useCaseApiSelected.reduce((acc, currentUseCase) => {
        const isSelected = currentUseCase.use_case_tags.some(tag => {
          const groupData = groupedMap[tag];
          return (
            groupData?.IOS?.carrier_selected ||
            groupData?.ANDROID?.carrier_selected
          );
        });

        if (isSelected) {
          acc.push(currentUseCase.api);
          uniqueGroups.add(currentUseCase.api.group);
        }

        return acc;
      }, [] as ISelectedUseCaseApi['api'][]);

      return {
        useCaseApiSelected,
        apisSelected,
        groupNames: Array.from(uniqueGroups),
      };
    },
  });
};

export const useBulkSelectUseCaseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ envId, ids, selected }: BulkSelectUseCasePayload) => {
      const promises = ids.map(id => selectUseCase(envId, id, selected));
      return Promise.all(promises);
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['get_use_cases_v2', variables.envId],
      });
      queryClient.invalidateQueries({
        queryKey: ['get_selected_api_tests_v2', variables.envId],
      });
    },
  });
};

export const useGetSelectedApiTests = (envId: string | undefined) => {
  return useQuery<IApiTestsResponse>({
    queryKey: ['get_selected_api_tests_v2', envId],
    queryFn: () => getSelectedApiTests(envId),
    retry: 1,
    enabled: Boolean(envId),
  });
};

export const useGetSelectedUseCaseTests = (envId: string | undefined) => {
  const { data } = useGetUseCasesV2(envId);
  const selectedTags = data?.selectedTags || [];

  return useQuery({
    queryKey: ['get_selected_use_case_tests_v2', envId, selectedTags],
    queryFn: () => getSelectedUseCaseTests(envId, selectedTags),
    retry: 1,
    enabled: Boolean(envId),
    select: responseData => ({
      useCaseTests: responseData,
      useCaseTags: Object.keys(responseData),
    }),
  });
};

export const useGetStats = (envId: string | undefined) => {
  const {
    data: selectedUseCases,
    isError: isUseCasesError,
    error: useCasesError,
  } = useGetUseCasesV2(envId);
  const selectedTags = selectedUseCases?.selectedTags || [];

  const query = useQuery({
    queryKey: ['get_stats_v2', envId, selectedTags],
    queryFn: () => getSelectedApiTests(envId),
    retry: 1,
    enabled: Boolean(envId) && Boolean(selectedUseCases),
    select: data => generateStats(data, selectedTags),
  });

  // This query stays disabled while the use cases it depends on are missing,
  // and a disabled react-query sits at `status: 'pending'` forever. Without
  // surfacing the upstream failure, a use cases error would leave consumers
  // stuck on their loading state with no error to react to.
  return {
    ...query,
    isError: query.isError || isUseCasesError,
    error: query.error || useCasesError,
  };
};

export const useGetApisTestData = (
  envId: string | undefined,
  apiTag: TUseCaseTags | TUseCasesApiTags,
  testCaseTag: TUseCaseTags,
  testType: TTestTypes,
) => {
  return useQuery({
    queryKey: ['get_api_test_data_v2', envId, testCaseTag],
    queryFn: () => getApisTestData(envId, apiTag, testCaseTag),
    retry: 0,
    enabled: Boolean(envId) && apiTag && testType === API,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
};

export const useGetUseCasesTestData = (
  envId: string | undefined,
  apiTag: TUseCaseTags | TUseCasesApiTags,
  testCaseTag: TUseCaseTags,
  testType: TTestTypes,
) => {
  return useQuery({
    queryKey: ['get_use_cases_test_data_v2', envId, testCaseTag],
    queryFn: () => getUseCaseTestData(envId, apiTag, testCaseTag),
    retry: 0,
    enabled: Boolean(envId) && apiTag && testType === USE_CASE,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
};

export const useUpdateUseCaseTestData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['update_use_case_test_data_v2'],
    mutationFn: ({ envId, payload }: IPostUseCaseVariables) =>
      postUseCaseTestData(envId, payload),
    onSuccess: (_, { envId, payload }) => {
      queryClient.invalidateQueries({
        queryKey: ['get_use_cases_test_data_v2', envId, payload.test_case_tag],
      });
    },
  });
};

export const useUpdateApiTestData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['update_api_test_data_v2'],
    mutationFn: ({ envId, payload }: IPostUseCaseVariables) =>
      postApiTestData(envId, payload),
    onSuccess: (_, { envId, payload }) => {
      queryClient.invalidateQueries({
        queryKey: ['get_api_test_data_v2', envId, payload.test_case_tag],
      });
    },
  });
};

export const useGetApiSearchSelectedOptions = (envId: string | undefined) => {
  return useQuery({
    queryKey: ['get_selected_options_v2', envId],
    queryFn: () => getSelectedOptions(envId),
    retry: 1,
    enabled: Boolean(envId),
    select: data => {
      return {
        data,
        dataMap: new Map(data.map(item => [item.id, item.label])) as Map<
          string,
          string
        >,
      };
    },
  });
};

//////  UTILS  //////
const generateStats = (
  apiTests: Record<string, any[]>,
  selectedTags: any[],
) => {
  const testCases = Object.values(apiTests);

  if (!testCases.length) {
    return {
      active_devices: null,
      use_cases_available: null,
      successfully_executed_use_cases: null,
      failed_use_cases: null,
    };
  }

  let successCount = 0;
  let failCount = 0;

  const activeDeviceImsis = new Set<string>();

  for (const tests of testCases) {
    for (const test of tests) {
      const execution = test?.api_test_execution;
      if (!execution) continue;

      if (execution.status === SUCCESS) {
        successCount++;
      } else if (execution.status === FAILED) {
        failCount++;
      }

      if (execution.data) {
        try {
          const parsedData = JSON.parse(execution.data);
          if (parsedData.IMSI) {
            activeDeviceImsis.add(parsedData.IMSI);
          }
        } catch (e) {
          // ignore invalid JSON
        }
      }
    }
  }

  return {
    active_devices: activeDeviceImsis.size,
    use_cases_available: selectedTags.length,
    successfully_executed_use_cases: successCount,
    failed_use_cases: failCount,
  };
};
